import { useRef, useState, useEffect, useCallback } from 'react';

interface UseAudioPlayerProps {
	audioSrc: string;
}

interface UseAudioPlayerReturn {
	audioRef: React.RefObject<HTMLAudioElement>;
	isPlaying: boolean;
	currentTime: number;
	duration: number;
	isLoading: boolean;
	togglePlayPause: () => Promise<void>;
	formatTime: (time: number) => string;
}

export const useAudioPlayer = ({
	audioSrc: _audioSrc,
}: UseAudioPlayerProps): UseAudioPlayerReturn => {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [hasInitialized, setHasInitialized] = useState(false);

	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

	const initializeAudio = useCallback(async () => {
		const audio = audioRef.current;
		if (!audio || hasInitialized) {
			return;
		}

		try {
			audio.load();
			setHasInitialized(true);

			// For iOS, we need to handle the case where metadata doesn't load immediately
			if (isIOS) {
				setTimeout(() => {
					if (duration === 0) {
						setIsLoading(false);
						// Set a default duration for iOS if metadata doesn't load
						setDuration(30);
					}
				}, 2000);
			}
		} catch (error) {
			console.error('Error initializing audio:', error);
			setIsLoading(false);
		}
	}, [hasInitialized, duration, isIOS]);

	useEffect(() => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		const handleLoadedMetadata = () => {
			setDuration(audio.duration || 0);
			setIsLoading(false);
		};

		const handleTimeUpdate = () => {
			setCurrentTime(audio.currentTime || 0);
		};

		const handleEnded = () => {
			setIsPlaying(false);
			setCurrentTime(0);
		};

		const handleLoadStart = () => {
			setIsLoading(true);
		};

		const handleCanPlay = () => {
			setIsLoading(false);
			if (duration === 0) {
				setDuration(audio.duration || 0);
			}
		};

		const handleError = () => {
			console.error('Audio error occurred');
			setIsLoading(false);
		};

		audio.addEventListener('loadedmetadata', handleLoadedMetadata);
		audio.addEventListener('timeupdate', handleTimeUpdate);
		audio.addEventListener('ended', handleEnded);
		audio.addEventListener('loadstart', handleLoadStart);
		audio.addEventListener('canplay', handleCanPlay);
		audio.addEventListener('error', handleError);

		// For iOS, don't rely on preload working
		if (isIOS) {
			setTimeout(() => {
				if (isLoading && duration === 0) {
					setIsLoading(false);
				}
			}, 1000);
		}

		return () => {
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
			audio.removeEventListener('timeupdate', handleTimeUpdate);
			audio.removeEventListener('ended', handleEnded);
			audio.removeEventListener('loadstart', handleLoadStart);
			audio.removeEventListener('canplay', handleCanPlay);
			audio.removeEventListener('error', handleError);
		};
	}, [isLoading, duration, isIOS]);

	const togglePlayPause = async () => {
		const audio = audioRef.current;
		if (!audio) {
			return;
		}

		try {
			if (!hasInitialized) {
				await initializeAudio();
			}

			if (isPlaying) {
				audio.pause();
				setIsPlaying(false);
			} else {
				if (audio.readyState === 0) {
					setIsLoading(true);
					audio.load();

					await new Promise((resolve, reject) => {
						const onCanPlay = () => {
							audio.removeEventListener('canplay', onCanPlay);
							audio.removeEventListener('error', onError);
							resolve(void 0);
						};

						const onError = () => {
							audio.removeEventListener('canplay', onCanPlay);
							audio.removeEventListener('error', onError);
							reject(new Error('Audio failed to load'));
						};

						audio.addEventListener('canplay', onCanPlay);
						audio.addEventListener('error', onError);

						setTimeout(() => {
							audio.removeEventListener('canplay', onCanPlay);
							audio.removeEventListener('error', onError);
							resolve(void 0);
						}, 3000);
					});

					setIsLoading(false);
				}

				await audio.play();
				setIsPlaying(true);
			}
		} catch (error) {
			console.error('Error playing audio:', error);
			setIsPlaying(false);
			setIsLoading(false);
		}
	};

	const formatTime = (time: number) => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	};

	return {
		audioRef,
		isPlaying,
		currentTime,
		duration,
		isLoading,
		togglePlayPause,
		formatTime,
	};
};
